import type { SpellType } from '../types/magic';

export interface SpellDefinition {
  type: SpellType;
  name: string;
  description: string;
  category: 'SELF' | 'OFFENSIVE';
  effect: 'INSTANT' | 'ENDURING';
  runeCostMultiplier: number;
  raceRestrictions?: string[]; // Only these races can cast
  /** SF-91e: spell creates an indefinite-duration effect (e.g. SHADOWLIGHT). */
  indefiniteDuration?: boolean;
}

// Formula constants from SF-16A (must match backend Property defaults)
export const SPELL_RUNE_COST_SIZE_MULT = 0.68;
export const SPELL_RUNE_COST_BASE = 680;

export const SPELLS: SpellDefinition[] = [
  // Self-cast enduring spells
  {
    type: 'MINOR_PROTECTION',
    name: 'Minor Protection',
    description: '+5% defensive ME, ~24 days',
    category: 'SELF',
    effect: 'ENDURING',
    runeCostMultiplier: 0.35,
  },
  {
    type: 'GREATER_PROTECTION',
    name: 'Greater Protection',
    description: '+5% defensive ME, ~48 days (Undead/Faery only)',
    category: 'SELF',
    effect: 'ENDURING',
    runeCostMultiplier: 0.45,
    raceRestrictions: ['UNDEAD', 'FAERY'],
  },
  {
    type: 'MAGIC_SHIELD',
    name: 'Magic Shield',
    description: '+20% defensive WPA, ~24 days',
    category: 'SELF',
    effect: 'ENDURING',
    runeCostMultiplier: 0.5,
  },
  {
    type: 'MAGES_FURY',
    name: "Mage's Fury",
    description: '+20% offensive WPA, -20% defensive WPA, ~16 days',
    category: 'SELF',
    effect: 'ENDURING',
    runeCostMultiplier: 1.4,
  },
  {
    type: 'SHADOWLIGHT',
    name: 'ShadowLight',
    description: 'Reveals attacker on next thievery (consumed on use)',
    category: 'SELF',
    effect: 'ENDURING',
    runeCostMultiplier: 1.9,
    indefiniteDuration: true,
  },
  // SF-91a: Paradise - instant land grant
  {
    type: 'PARADISE',
    name: 'Paradise',
    description: 'Instantly grants 1-3% of current land as barren acres (blocked in War/Fortified)',
    category: 'SELF',
    effect: 'INSTANT',
    runeCostMultiplier: 3.0,
  },
  // Offensive instant spells
  {
    type: 'FIREBALL',
    name: 'Fireball',
    description: 'Kills 4-7% peasants',
    category: 'OFFENSIVE',
    effect: 'INSTANT',
    runeCostMultiplier: 1.5,
  },
  {
    type: 'LIGHTNING_STRIKE',
    name: 'Lightning Strike',
    description: 'Destroys 30-65% runes',
    category: 'OFFENSIVE',
    effect: 'INSTANT',
    runeCostMultiplier: 1.55,
  },
  {
    type: 'CRYSTAL_BALL',
    name: 'Crystal Ball',
    description: 'View target throne, captures intel (85% success)',
    category: 'OFFENSIVE',
    effect: 'INSTANT',
    runeCostMultiplier: 0.8,
  },
  // SF-91d: Crystal Eye — kingdom event log for the lookback window
  {
    type: 'CRYSTAL_EYE',
    name: 'Crystal Eye',
    description: 'Reveal target kingdom\'s recent news log (85% success)',
    category: 'OFFENSIVE',
    effect: 'INSTANT',
    runeCostMultiplier: 3.0,
  },
  // Offensive enduring spells
  {
    type: 'CHASTITY',
    name: 'Chastity',
    description: '0% birth rate, ~18 days',
    category: 'OFFENSIVE',
    effect: 'ENDURING',
    runeCostMultiplier: 1.6,
  },
  // SF-91b: Pitfalls — Elf/Faery only, HOSTILE required
  {
    type: 'PITFALLS',
    name: 'Pitfalls',
    description: '+25% defensive losses on target, ~12 days (Elf/Faery only, HOSTILE required)',
    category: 'OFFENSIVE',
    effect: 'ENDURING',
    runeCostMultiplier: 1.5,
    raceRestrictions: ['ELF', 'FAERY'],
  },
  // SF-91c: Explosions — all races, no relationship gate
  {
    type: 'EXPLOSIONS',
    name: 'Explosions',
    description: '50% chance to reduce aid shipments to/from target by 20-45%, ~12 days',
    category: 'OFFENSIVE',
    effect: 'ENDURING',
    runeCostMultiplier: 1.8,
  },
  // SF-93: SF-59 backend spells surfaced in the UI
  // Self-cast enduring
  {
    type: 'FERTILE_LANDS',
    name: 'Fertile Lands',
    description: '+25% food production, ~24 days. Cancels Drought',
    category: 'SELF',
    effect: 'ENDURING',
    runeCostMultiplier: 0.5,
  },
  {
    type: 'NATURES_BLESSING',
    name: "Nature's Blessing",
    description: 'Blocks Storms/Drought + 20% chance to cure Plague on cast, ~16 days',
    category: 'SELF',
    effect: 'ENDURING',
    runeCostMultiplier: 0.6,
  },
  {
    type: 'LOVE_AND_PEACE',
    name: 'Love & Peace',
    description: '+0.75% birth rate, ~22 days',
    category: 'SELF',
    effect: 'ENDURING',
    runeCostMultiplier: 0.7,
  },
  {
    type: 'BUILDERS_BOON',
    name: "Builder's Boon",
    description: '-10% construction time, ~24 days',
    category: 'SELF',
    effect: 'ENDURING',
    runeCostMultiplier: 1.0,
  },
  {
    type: 'INSPIRE_ARMY',
    name: 'Inspire Army',
    description: '-15% wages, -20% training time, ~20 days',
    category: 'SELF',
    effect: 'ENDURING',
    runeCostMultiplier: 1.1,
  },
  {
    type: 'CLEAR_SIGHT',
    name: 'Clear Sight',
    description: 'Flat 25% thief catch chance, ~18 days (Dwarf/Faery only)',
    category: 'SELF',
    effect: 'ENDURING',
    runeCostMultiplier: 1.4,
    raceRestrictions: ['DWARF', 'FAERY'],
  },
  {
    type: 'FOUNTAIN_OF_KNOWLEDGE',
    name: 'Fountain of Knowledge',
    description: '+10% science bonus, ~24 days (Human/Faery only)',
    category: 'SELF',
    effect: 'ENDURING',
    runeCostMultiplier: 1.55,
    raceRestrictions: ['HUMAN', 'FAERY'],
  },
  {
    type: 'PATRIOTISM',
    name: 'Patriotism',
    description: '+30% draft speed, ~20 days',
    category: 'SELF',
    effect: 'ENDURING',
    runeCostMultiplier: 2.0,
  },
  {
    type: 'WAR_SPOILS',
    name: 'War Spoils',
    description: 'Traditional March land arrives immediately instead of waiting for your army to return, ~3 days (Human/Faery only)',
    category: 'SELF',
    effect: 'ENDURING',
    runeCostMultiplier: 1.5,
    raceRestrictions: ['HUMAN', 'FAERY'],
  },
  {
    type: 'MYSTIC_AURA',
    name: 'Mystic Aura',
    description: 'Single-use spell shield, consumed on next offensive cast against you (does not block intel; Avian/Faery only)',
    category: 'SELF',
    effect: 'ENDURING',
    runeCostMultiplier: 0.5,
    raceRestrictions: ['AVIAN', 'FAERY'],
    indefiniteDuration: true,
  },
  {
    type: 'ANONYMITY',
    name: 'Anonymity',
    description: 'Single-use attack cloak — hide province name on next attack, no honor, +15% attack time (kingdom still visible)',
    category: 'SELF',
    effect: 'ENDURING',
    runeCostMultiplier: 1.3,
    indefiniteDuration: true,
  },
  // Self-cast instant
  {
    type: 'TREE_OF_GOLD',
    name: 'Tree of Gold',
    description: 'Instant gold grant 40-80% of daily income (Human/Faery only)',
    category: 'SELF',
    effect: 'INSTANT',
    runeCostMultiplier: 0.8,
    raceRestrictions: ['HUMAN', 'FAERY'],
  },
  // Offensive enduring
  {
    type: 'STORMS',
    name: 'Storms',
    description: '1.5% population kill per day, ~12 days. Cancels Drought',
    category: 'OFFENSIVE',
    effect: 'ENDURING',
    runeCostMultiplier: 0.8,
  },
  {
    type: 'DROUGHT',
    name: 'Drought',
    description: '-25% food, -15% draft speed, ~12 days. Cancels Storms',
    category: 'OFFENSIVE',
    effect: 'ENDURING',
    runeCostMultiplier: 1.0,
  },
  {
    type: 'GREED',
    name: 'Greed',
    description: '+25% wages and draft cost on target, ~12 days',
    category: 'OFFENSIVE',
    effect: 'ENDURING',
    runeCostMultiplier: 1.3,
  },
  // Offensive instant
  {
    type: 'EXPOSE_THIEVES',
    name: 'Expose Thieves',
    description: 'Reduces target stealth to 93% on next thievery roll',
    category: 'OFFENSIVE',
    effect: 'INSTANT',
    runeCostMultiplier: 1.2,
  },
];

export function calculateRuneCost(totalAcres: number, spell: SpellDefinition): number {
  return Math.floor(
    (SPELL_RUNE_COST_SIZE_MULT * totalAcres + SPELL_RUNE_COST_BASE) *
      spell.runeCostMultiplier
  );
}

export function getSpellByType(type: SpellType): SpellDefinition | undefined {
  return SPELLS.find((s) => s.type === type);
}

export function filterSpellsByCategory(
  category: 'SELF' | 'OFFENSIVE',
  race?: string
): SpellDefinition[] {
  return SPELLS.filter((spell) => {
    if (spell.category !== category) return false;
    if (spell.raceRestrictions && race) {
      return spell.raceRestrictions.includes(race);
    }
    return true;
  });
}

// SF-105: intel spells are exempt from the protection gate on the backend
// (CastSpellAction.validateIntelTarget(), not validateTarget()) even though the frontend's
// SpellDefinition.category tags them 'OFFENSIVE' (they need a target, unlike self-cast spells).
// SF-148: relocated from Magic.tsx — un-exportable from a page .tsx file per
// react-refresh/only-export-components, and both Magic shells need it.
export const INTEL_SPELL_TYPES: SpellType[] = ['CRYSTAL_BALL', 'CRYSTAL_EYE'];

export interface SpellOptgroup {
  label: string;
  spells: SpellDefinition[];
}

const OPTGROUP_BUCKETS: { label: string; category: 'SELF' | 'OFFENSIVE'; effect: 'INSTANT' | 'ENDURING' }[] = [
  { label: 'Self-Cast — Instant', category: 'SELF', effect: 'INSTANT' },
  { label: 'Self-Cast — Enduring', category: 'SELF', effect: 'ENDURING' },
  { label: 'Offensive — Instant', category: 'OFFENSIVE', effect: 'INSTANT' },
  { label: 'Offensive — Enduring', category: 'OFFENSIVE', effect: 'ENDURING' },
];

/**
 * SF-148: pre-groups a spell list into the four fixed optgroup buckets (Self-Cast/Offensive x
 * Instant/Enduring), omitting empty groups. Extracted so the grouping block isn't duplicated
 * across MobileMagicShell and DesktopMagicShell.
 */
export function groupSpellsForOptgroups(spells: SpellDefinition[]): SpellOptgroup[] {
  return OPTGROUP_BUCKETS
    .map(({ label, category, effect }) => ({
      label,
      spells: spells.filter((s) => s.category === category && s.effect === effect),
    }))
    .filter((group) => group.spells.length > 0);
}
